import { useNavigate } from 'react-router-dom'
import { Gear } from '@phosphor-icons/react'
import { DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import { RUN_STATUSES, STEP_CODES, stepLabel, useAllMachines, useRuns, type RunRow } from '../../api/process'
import { useCrops } from '../../api/reference'
import { useSites } from '../../api/store'
import { DateFilter, FilterBar, SelectFilter } from '../../components/FilterBar'
import { ConsolePage } from '../../components/Layout'
import { Code, Kg, RefLink, Status, Stream, When } from '../../components/bits'
import { LeftOverCell, YieldCell } from '../../components/process/ProcessBits'
import { statusLabel } from '../../labels'
import { useUrlState } from '../../lib/useUrlState'

const LIMIT = 25

/** Machine runs: every run with its balance, origin, operator and open problems. */
export function RunsPage() {
  const navigate = useNavigate()
  const [f, setF, { reset, active }] = useUrlState({
    status: '',
    locationId: '',
    equipmentId: '',
    cropId: '',
    stepCode: '',
    stream: '',
    from: '',
    to: '',
    page: 1,
  })
  const crops = useCrops()
  const sites = useSites()
  const machines = useAllMachines()
  const q = useRuns({
    page: f.page,
    limit: LIMIT,
    status: f.status || undefined,
    locationId: f.locationId || undefined,
    equipmentId: f.equipmentId || undefined,
    cropId: f.cropId || undefined,
    stepCode: f.stepCode || undefined,
    stream: f.stream || undefined,
    from: f.from || undefined,
    to: f.to || undefined,
  })

  const columns: Column<RunRow>[] = [
    {
      id: 'code',
      header: 'Run',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <Code>{r.code}</Code>
          <span className="text-[12px] text-muted">
            <When at={r.openedAt} />
          </span>
        </span>
      ),
    },
    { id: 'step', header: 'Step', cell: (r) => stepLabel(r.stepCode) },
    {
      id: 'machine',
      header: 'Machine',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <Code>{r.equipmentCode ?? '—'}</Code>
          <span className="text-[12px] text-muted">{r.locationName ?? r.locationCode ?? ''}</span>
        </span>
      ),
      hideBelow: 'md',
    },
    {
      id: 'origin',
      header: 'Origin',
      cell: (r) =>
        r.originUnitId ? (
          <span className="flex flex-col leading-tight">
            <RefLink to={`/c/batches/${r.originUnitId}`}>
              {r.originUnitCode ?? 'Origin'}
            </RefLink>
            {r.originFarmerCode && <span className="font-mono text-[11.5px] text-muted">{r.originFarmerCode}</span>}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
      hideBelow: 'lg',
    },
    { id: 'operator', header: 'Operator', cell: (r) => r.operator?.name || '—', hideBelow: 'lg' },
    { id: 'in', header: 'In', align: 'right', cell: (r) => <Kg grams={r.balance.inputG} /> },
    { id: 'out', header: 'Out', align: 'right', cell: (r) => <Kg grams={r.balance.outputG} />, hideBelow: 'sm' },
    { id: 'left', header: 'Left over', align: 'right', cell: (r) => <LeftOverCell balance={r.balance} />, hideBelow: 'md' },
    { id: 'yield', header: 'Yield', align: 'right', cell: (r) => <YieldCell balance={r.balance} /> },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <span className="flex flex-wrap items-center gap-1">
          <Status status={r.status} />
          {r.stream === 'CONV' && <Stream stream="CONV" />}
          {r.openProblems > 0 && (
            <Status status="HOLD" label={`${r.openProblems} open ${r.openProblems === 1 ? 'problem' : 'problems'}`} />
          )}
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Process"
      title="Machine runs"
      description="Every run on a processing machine: what went in, what came out, what was left over and whether the yield sits in the machine's range. A run that does not balance waits for a supervisor's decision and its batches stay on hold."
    >
      <DataTable<RunRow>
        caption="Machine runs"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/runs/${r.id}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Status"
              value={f.status}
              onChange={(status) => setF({ status })}
              options={RUN_STATUSES.map((s) => ({ value: s, label: statusLabel(s).en }))}
            />
            <SelectFilter
              label="Site"
              value={f.locationId}
              onChange={(locationId) => setF({ locationId })}
              options={(sites.data ?? []).map((s) => ({ value: s.id, label: s.name || s.code }))}
            />
            <SelectFilter
              label="Machine"
              value={f.equipmentId}
              onChange={(equipmentId) => setF({ equipmentId })}
              options={(machines.data ?? []).map((m) => ({ value: m.id, label: `${m.code} · ${m.name}` }))}
            />
            <SelectFilter label="Crop" value={f.cropId} onChange={(cropId) => setF({ cropId })} options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
            <SelectFilter label="Step" value={f.stepCode} onChange={(stepCode) => setF({ stepCode })} options={STEP_CODES.map((s) => ({ value: s, label: stepLabel(s) }))} />
            <SelectFilter
              label="Stream"
              value={f.stream}
              onChange={(stream) => setF({ stream })}
              options={[
                { value: 'MAIN', label: 'Good' },
                { value: 'CONV', label: 'Ordinary' },
              ]}
            />
            <DateFilter label="Opened from" value={f.from} onChange={(from) => setF({ from })} />
            <DateFilter label="Opened to" value={f.to} onChange={(to) => setF({ to })} />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<Gear />}
            title={active ? 'No runs match' : 'No machine runs yet'}
            description="Runs appear when an operator opens one on the phone at the machine."
          />
        }
      />
    </ConsolePage>
  )
}
